import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiTrendingDown, FiTrendingUp } = FiIcons;

const ResultsSection = ({ results }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(value));
  };

  const formatCurrencyWithSign = (value) => {
    const formatted = formatCurrency(value);
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const monthlyIsSavings = results.monthlyDifference < 0;
  const totalInterestIsSavings = results.totalInterestDifference < 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
    >
      <div className="flex items-center gap-3 mb-8">
        <SafeIcon icon={FiDollarSign} className="text-2xl text-indigo-600" />
        <h3 className="text-2xl font-bold text-gray-900">Comparison Results</h3>
      </div>

      <div className="space-y-6">
        {/* Monthly Payment Difference */}
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <SafeIcon 
              icon={monthlyIsSavings ? FiTrendingDown : FiTrendingUp} 
              className={`text-xl ${monthlyIsSavings ? 'text-success-600' : 'text-warning-600'}`} 
            />
            <h4 className="text-lg font-semibold text-gray-900">
              Monthly Payment Difference
            </h4>
          </div>
          <div className={`text-3xl font-bold ${monthlyIsSavings ? 'text-success-600' : 'text-warning-600'}`}>
            {formatCurrencyWithSign(results.monthlyDifference)}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {monthlyIsSavings ? 'Monthly savings with refinance' : 'Monthly increase with refinance'}
          </p>
        </div>

        {/* Total Interest Difference */}
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <SafeIcon 
              icon={totalInterestIsSavings ? FiTrendingDown : FiTrendingUp} 
              className={`text-xl ${totalInterestIsSavings ? 'text-success-600' : 'text-warning-600'}`} 
            />
            <h4 className="text-lg font-semibold text-gray-900">
              Total Interest Difference
            </h4>
          </div>
          <div className={`text-3xl font-bold ${totalInterestIsSavings ? 'text-success-600' : 'text-warning-600'}`}>
            {formatCurrencyWithSign(results.totalInterestDifference)}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {totalInterestIsSavings ? 'Total interest savings over loan term' : 'Additional interest over loan term'}
          </p>
        </div>

        {/* Summary */}
        <div className="border-t pt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment Breakdown</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Current Monthly Payment</p>
              <p className="font-semibold text-gray-900">
                {formatCurrency(results.current.monthlyPayment)}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Refi Monthly Payment</p>
              <p className="font-semibold text-gray-900">
                {formatCurrency(results.refi.monthlyPayment)}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Current Total Interest</p>
              <p className="font-semibold text-gray-900">
                {formatCurrency(results.current.totalInterest)}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Refi Total Interest</p>
              <p className="font-semibold text-gray-900">
                {formatCurrency(results.refi.totalInterest)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultsSection;